/*
 * Peças comuns das mensagens: se o recurso vale, as caixas, os links do
 * perfil e o que a conversa do flarum/messages tem a mais pelo tema.
 *
 * O flarum/messages é opcional e os módulos dele não são importados com
 * `ext:` (o tema quebraria sem ele): as conversas são lidas pelos próprios
 * métodos do modelo, que existem quando ele está ligado, e os componentes
 * carregados sob demanda são estendidos pelo nome do módulo.
 */
import app from 'flarum/forum/app';
import { forumBool, isExtensionEnabled, t } from '../../utils/settings';

/** O modelo `dialogs` do flarum/messages, só com o que o tema usa. */

/** As abas, na ordem. */
export const BOXES = ['inbox', 'sent', 'new', 'unread', 'archive'];

/** O flarum/messages está ligado e o visual de mensagens do tema também. */
export function messagesOn() {
  return isExtensionEnabled('flarum-messages') && !!app.dialogs && forumBool('canelaMessagesEnabled', true);
}

/**
 * O backend do tema (extend/messages.php) está carregado: caixas, arquivo,
 * título e "sair". Sem ele, só o que o flarum/messages faz sozinho.
 */
export function boxesOn() {
  return !!app.forum?.attribute('canelaMessagesBoxes');
}

/** As caixas que dá para mostrar agora (sem o backend, só as do flarum/messages). */
export function availableBoxes() {
  if (!boxesOn()) return ['inbox', 'unread'];
  return messageGroup()?.member ? [...BOXES, 'group'] : BOXES;
}

/**
 * A caixa da equipe (backend do tema, migration de grupos): o grupo, se quem
 * vê é da equipe, e alguém dela para receber a mensagem "à equipe".
 */
export function messageGroup() {
  const value = app.forum?.attribute('canelaMessageGroup');
  return value && typeof value === 'object' && value.id ? value : null;
}

/** O grupo (modelo) da caixa da equipe, do store (o fórum já traz os grupos). */
export function messageGroupModel() {
  const group = messageGroup();
  return group ? app.store.getById('groups', String(group.id)) || null : null;
}

/** O nome do grupo no chip (o plural do grupo). */
export function groupLabel(group) {
  return group.namePlural() || group.nameSingular();
}

/** A rota da aba "Mensagens" do perfil, existindo. */
export function hasProfileRoute() {
  return !!app.routes['user.messages'];
}

/**
 * O endereço de uma caixa. `/u/:username/messages/:box` só abre direto com o
 * backend do tema (a rota do PHP é dele); sem ele a caixa vai na query.
 */
export function boxHref(user, box = 'inbox') {
  if (!hasProfileRoute()) return app.route('messages');
  const username = user.slug();
  if (box === 'inbox') return app.route('user.messages', {
    username
  });
  return boxesOn() ? app.route('user.messages.box', {
    username,
    box
  }) : app.route('user.messages', {
    username,
    box
  });
}

/** "Minhas mensagens": a caixa de entrada de quem está logado. */
export function myMessagesHref() {
  const user = app.session.user;
  return user ? boxHref(user) : app.route('messages');
}

/** O endereço de uma conversa (o "tópico" da mensagem). */
export function dialogHref(dialog, near) {
  const helper = app.route.dialog;
  return helper ? helper(dialog, near) : app.route('messages');
}

/** O título do tópico de mensagem: o do composer ou o "Conversando com X". */
export function dialogTitle(dialog) {
  return dialog.attribute('canelaTitle') || dialog.title() || t('messages.untitled');
}
export function isArchived(dialog) {
  return !!dialog.attribute('canelaArchived');
}

/** Os participantes, sem os que sumiram do store (conta excluída). */
export function participants(dialog) {
  const users = dialog.users();
  return (users ? users : []).filter(user => !!user);
}

/**
 * A conversa com o usuário do sistema (as mensagens automáticas): só de ida,
 * ninguém lê a caixa dele, então ela não tem resposta.
 */
export function isSystemDialog(dialog) {
  return participants(dialog).some(user => !!user.attribute('canelaIsSystem'));
}

/**
 * "Novo (n)" e "Não lidos (n)". Com o backend do tema vêm separados e sem o
 * arquivo; sem ele, o flarum/messages só sabe o total de conversas com não
 * lidas, que fica todo em "Não lidos".
 */
export function boxCounts() {
  const user = app.session.user;
  if (!user) return {
    new: 0,
    unread: 0,
    group: 0
  };
  const counts = user.attribute('canelaDialogCounts');
  if (counts && typeof counts === 'object') return {
    new: Number(counts.new) || 0,
    unread: Number(counts.unread) || 0,
    group: Number(counts.group) || 0
  };
  return {
    new: 0,
    unread: Number(user.attribute('unreadDialogCount')) || 0,
    group: 0
  };
}

/** O total que "Minhas mensagens" e a "Caixa de entrada" mostram. */
export function totalUnread() {
  const counts = boxCounts();

  // A equipe vê também o que chegou na caixa do grupo.
  return counts.new + counts.unread + counts.group;
}

/** As visualizações da conversa; `null` sem a contagem no banco. */
export function dialogViews(dialog) {
  const value = dialog.attribute('viewCount');
  return typeof value === 'number' ? value : null;
}

/**
 * Os grupos da equipe (administradores e moderadores) dos outros
 * participantes: o chip "👥 moderators" sob o título. No flarum/messages a
 * conversa é entre pessoas, então o chip diz de que equipe é quem está nela.
 */
export function staffGroups(dialog) {
  const groupId = dialog.attribute('canelaGroupId');

  // Mensagem da caixa de um grupo: o chip é o do grupo.
  if (groupId) {
    const group = app.store.getById('groups', String(groupId));
    return group ? [group] : [];
  }
  const me = app.session.user;
  const seen = new Set();
  const groups = [];
  participants(dialog).filter(user => user !== me).forEach(user => {
    (user.groups() || []).forEach(group => {
      const id = group?.id();
      if (!group || !id || seen.has(id)) return;
      if (id !== '1' && id !== '4') return;
      seen.add(id);
      groups.push(group);
    });
  });
  return groups;
}

/** O número de mensagens da conversa: o `number` da última, que é sequencial. */
export function messageCount(dialog) {
  const last = dialog.lastMessage();
  return last ? last.number() || 0 : 0;
}

/**
 * Importa um componente carregado sob demanda já no formato `{ default }` que
 * o `composer.load` espera. O `asyncModuleImport` devolve a própria classe
 * quando o módulo já está registrado — e uma classe que herda do ComposerBody
 * herda também o `default` estático que o core pendura nele, apontando para o
 * ComposerBody: o composer abria vazio, com o corpo errado.
 */
export function lazyComponent(path) {
  return flarum.reg.asyncModuleImport(path).then(mod => ({
    default: typeof mod === 'function' ? mod : Object.prototype.hasOwnProperty.call(mod, 'default') ? mod.default : mod
  }));
}

/**
 * Abre o composer de mensagem do flarum/messages (o MessageComposer vem num
 * pedaço carregado sob demanda, como o ComposerBody do core).
 */
export function openMessageComposer(attrs = {}) {
  return flarum.reg.asyncModuleImport('flarum/forum/components/ComposerBody').then(() => app.composer.load(() => lazyComponent('ext:flarum/messages/forum/components/MessageComposer'), {
    user: app.session.user,
    ...attrs
  }).then(() => app.composer.show()));
}

/** Quem pode começar uma mensagem (a permissão do flarum/messages). */
export function canSendMessages() {
  return !!app.session.user?.attribute('canSendAnyMessage');
}

/**
 * "Mensagem a um membro da equipe": abre a mensagem nova já endereçada ao
 * grupo da equipe (o chip do grupo no destinatário). Sem a caixa da equipe,
 * leva para a lista da equipe no Sobre.
 */
export function messageStaff(event) {
  const group = messageGroup();
  if (!group || !canSendMessages()) {
    const routes = app.routes;
    if (!event) m.route.set(routes['canela.about'] ? app.route('canela.about') : routes.about ? app.route('about') : app.route('index'));
    return;
  }
  event?.preventDefault();
  openMessageComposer().then(() => {
    const fields = app.composer.fields;
    if (fields?.canelaGroup) fields.canelaGroup(group.id);
    m.redraw();
  }).catch(() => {});
}
flarum.reg.add('ramon-canela', 'forum/features/messages/util', { BOXES: BOXES,messagesOn: messagesOn,boxesOn: boxesOn,availableBoxes: availableBoxes,messageGroup: messageGroup,messageGroupModel: messageGroupModel,groupLabel: groupLabel,hasProfileRoute: hasProfileRoute,boxHref: boxHref,myMessagesHref: myMessagesHref,dialogHref: dialogHref,dialogTitle: dialogTitle,isArchived: isArchived,participants: participants,isSystemDialog: isSystemDialog,boxCounts: boxCounts,totalUnread: totalUnread,dialogViews: dialogViews,staffGroups: staffGroups,messageCount: messageCount,lazyComponent: lazyComponent,openMessageComposer: openMessageComposer,canSendMessages: canSendMessages,messageStaff: messageStaff, });